import React from 'react';
import { translate } from 'translations/i18n';
import Steven from 'helpers/steven';
import toast from 'helpers/toast';
import ProjectTasksList from 'components/projects/projectTasks/ProjectTasksList.jsx';
import {
  ASSET_TYPE,
  useGetAssetProjectTasks,
  useCreateAssetProjectTask,
  getSourceFromTaskAssets,
} from 'javascripts/data/v3';
import { TaskImageObject, TaskVideoObject } from 'data/v3/data-contracts';
import styled from 'styled-components';
import { AssetModalSidebarHeader } from './shared/styles';
import { Button, Notice } from '@companycam/slab-web';
import CCTooltip from 'components/shared/DesignSystem/CCFloating/CCTooltip';
import useLocalStorage from 'javascripts/hooks/useLocalStorage';
import { useCurrentUser } from 'javascripts/hooks';
import { NewBadge } from 'components/shared/Badge';
import OutsideClickObserver from 'components/shared/OutsideClickObserver';

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--cc_size_spacing_s);
  max-width: 300px;

  p {
    font-weight: 400;
    line-height: 1.4;
  }

  strong {
    font-weight: 700;
  }
`;

const AssetModalTasksContainer = styled.div`
  margin: var(--cc_size_spacing_m);
  margin-top: 0;
  background: var(--cc_color_background_2);
  border-radius: var(--cc_size_border_radius_l);
  padding: var(--cc_size_spacing_m);
  // needed to offset the top margin on the TaskHeader (16 + 10)
  padding-top: 26px;
`;

const TasksHeader = styled.div`
  // needed to position the CCTooltip popover correctly
  margin-top: -10px;
  margin-bottom: var(--cc_size_spacing_xs);
  font-size: var(--cc_size_text_m);
  font-family: 'Averta', system-ui, sans-serif;
  font-weight: 600;
`;

const ErrorLabel = styled.span`
  font-family: system-ui, sans-serif;
  font-weight: 400;
  font-size: var(--cc_size_text_xs);
  color: var(--cc_color_text_nonessential);
  margin-top: var(--cc_size_spacing_m);
  margin-left: var(--cc_size_spacing_l);
`;

type ProjectTasksProps = {
  assetDbId: TaskImageObject['id'] | TaskVideoObject['id'];
  assetType: string;
  projectInfo: {
    nodeId: string;
    databaseId: number;
    formattedLine1: string;
    formattedLine2?: string;
  };
};

const ProjectTasks = ({
  assetDbId,
  assetType,
  projectInfo,
}: ProjectTasksProps) => {
  const currentUser = useCurrentUser();

  const [
    hasOnboardedProjectTasksPhotoModal,
    setHasOnboardedProjectTasksPhotoModal,
  ] = useLocalStorage(
    `hasOnboardedProjectTasksPhotoModal:${currentUser.node_id}`,
    false,
  );

  const {
    incompleteTasks,
    completedTasks,
    addNewTask,
    addNewTaskAtEnd,
    removeNewTask,
    loading,
    error,
  } = useGetAssetProjectTasks({
    projectDbId: projectInfo.databaseId,
    assetDbId,
    assetType,
  });

  const createProjectTask = useCreateAssetProjectTask({
    projectDbId: projectInfo.databaseId,
    assetDbId,
    assetType,
    onSuccess: (createdTask) => {
      const createdTaskId = createdTask?.id;

      const source = getSourceFromTaskAssets({
        task: createdTask,
      });

      if (createdTaskId) {
        Steven.entityCrud({
          entity_id: createdTaskId.toString(),
          entity_type: 'project_task',
          action: 'created',
          source,
        });
      }
    },
    onError: () => {
      toast.error(translate('projectTasks.createTaskErrorMsg'));
    },
  });

  const isPhoto = assetType === ASSET_TYPE.PHOTO;
  const isVideo = assetType === ASSET_TYPE.VIDEO;

  const isVideoOrPhoto = isPhoto || isVideo;

  if (error) {
    return (
      <>
        <AssetModalSidebarHeader>
          {translate('projects.show.projectSidebar.tasks')}
        </AssetModalSidebarHeader>
        <ErrorLabel>
          {translate('photoProjectTasks.getErrorMessage')}
        </ErrorLabel>
      </>
    );
  }

  return (
    <AssetModalTasksContainer>
      {!hasOnboardedProjectTasksPhotoModal && !loading && isVideoOrPhoto ? (
        <OutsideClickObserver
          onClickOutside={() => {
            setHasOnboardedProjectTasksPhotoModal(true);
          }}
        >
          <CCTooltip
            position="left"
            triggerStyle="always"
            offset={32}
            onClickOutside={() => setHasOnboardedProjectTasksPhotoModal(true)}
          >
            <TooltipContainer>
              <NewBadge />
              <p>{translate('photoProjectTasks.tooltipOnboardingOne')}</p>
              <p>{translate('photoProjectTasks.tooltipOnboardingTwo')}</p>
              <Button
                color="secondary"
                design="outline"
                size="small"
                onClick={() => setHasOnboardedProjectTasksPhotoModal(true)}
              >
                {translate('common.gotIt')}
              </Button>
            </TooltipContainer>
          </CCTooltip>
        </OutsideClickObserver>
      ) : null}
      <TasksHeader data-testid="asset__tasks-header">
        {translate('projects.show.projectSidebar.tasks')}
      </TasksHeader>
      {isVideoOrPhoto ? (
        <ProjectTasksList
          loading={loading}
          incompleteTasks={incompleteTasks}
          completedTasks={completedTasks}
          createProjectTask={createProjectTask}
          removeNewTask={removeNewTask}
          addNewTask={addNewTask}
          addNewTaskAtEnd={addNewTaskAtEnd}
          projectInfo={projectInfo}
          assetDbId={assetDbId}
          isGalleryView
        />
      ) : (
        <Notice style={{ background: 'transparent', padding: 0 }}>
          {translate('photoProjectTasks.videoNotSupportedNotice')}
        </Notice>
      )}
    </AssetModalTasksContainer>
  );
};

export default ProjectTasks;
